import React, { createContext, useState, useEffect, useContext } from 'react';
import { ordersApi } from '../network/apis/orders';
import { useAuth } from './AuthContext';
import { toast } from 'react-toastify';
import Paystack from '@paystack/inline-js';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
    const [cartItems, setCartItems] = useState([]);
    const [showCart, setShowCart] = useState(false);
    const { user, isAuthenticated } = useAuth();


    // Load cart from localStorage on component mount
    useEffect(() => {
        const savedCart = localStorage.getItem('eaquaCart');
        if (savedCart) {
            setCartItems(JSON.parse(savedCart));
        }
    }, []);
    
    // Save cart to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('eaquaCart', JSON.stringify(cartItems));
    }, [cartItems]);
    

    const addToCart = (product) => {
        setCartItems(prevItems => {
            const existingItem = prevItems.find(item => item.id === product.id);
            if (existingItem) {
                return prevItems.map(item =>
                    item.id === product.id 
                        ? {...item, quantity: (item.quantity || 1) + 1}
                        : item
                );
            }
            return [...prevItems, {...product, quantity: 1}];
        });
        toast.success(`${product.name} added to cart`);
    };

    const removeFromCart = (productId) => {
        setCartItems(prevItems => prevItems.filter(item => item.id !== productId));
        toast.info("Item removed from cart");
    };

    const getCartCount = () => {
        return cartItems.reduce((total, item) => total + (item.quantity || 1), 0);
    };

    const updateQuantity = (productId, quantity) => {
        if (quantity < 1) return;
        
        setCartItems(prevItems =>
            prevItems.map(item =>
                item.id === productId
                    ? { ...item, quantity }
                    : item
            )
        );
    };

    const calculateTotal = () => {
        return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
    };
    
    const clearCart = () => {
        setCartItems([]);
        localStorage.removeItem('eaquaCart');
    };
    
    const handleCheckout = async () => {
        try {
            if (!isAuthenticated) {
                toast.error("Please login to proceed with checkout");
                return;
            }
            
            if (cartItems.length === 0) {
                toast.error("Your cart is empty");
                return;
            }
            
            // 1. Create order in your backend
            // eslint-disable-next-line no-unused-vars
            const orderData = {
                items: cartItems.map(item => ({
                    product_id: item.id,
                    quantity: item.quantity,
                    unit_price: item.price
                })),
                total_amount: calculateTotal(),
                shipping_address: user.address || "",
                payment_method: "paystack"
            };
            
            // const orderResponse = await ordersApi.createOrder(orderData);
            // const order = orderResponse.data.data;
            const order = {
                id : "r65"
            }
            
            // 2. Initialize Paystack payment
            const paystack = new Paystack();
            paystack.newTransaction({
                key: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY, // Replace with your actual public key
                email: user.email,
                amount: calculateTotal() * 100, // Paystack expects amount in kobo (multiply by 100)
                currency: 'NGN',
                ref: `eaquahealth-${order.id}-${Date.now()}`, // Unique reference for this transaction
                metadata: {
                    order_id: order.id,
                    custom_fields: [
                        {
                            display_name: "Order ID",
                            variable_name: "order_id",
                            value: order.id
                        },
                        {
                            display_name: "Customer Name",
                            variable_name: "customer_name",
                            value: `${user.first_name} ${user.last_name}`
                        }
                    ]
                },
                onSuccess: (transaction) => {
                    // Verify payment on your backend
                    verifyPayment(order.id, transaction.reference);
                },
                onCancel: () => {
                    toast.info("Payment cancelled");
                }
            });
            
        } catch (error) {
            console.error("Checkout error:", error);
            toast.error(error.response?.data?.message || "Failed to process checkout");
        }
    };
    
    const verifyPayment = async (orderId, reference) => {
        try {
            await ordersApi.verifyPayment(orderId, reference);
            toast.success("Payment successful! Your order has been placed.");
            clearCart();
            setShowCart(false);
            // Redirect to order confirmation page
            window.location.href = `/orders/${orderId}/confirmation`;
        } catch (error) {
            console.error("Payment verification error:", error);
            toast.error("Payment verification failed. Please contact support.");
        }
    };
    


    return (
        <CartContext.Provider value={{
            cartItems,
            showCart,
            setShowCart,
            removeFromCart,
            updateQuantity,
            // setShowCart,
            addToCart,
            // removeFromCart,
            getCartCount,
            calculateTotal,
            clearCart,
            handleCheckout
        }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => useContext(CartContext);